import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Tag, Calendar, DollarSign, Percent, X, ChevronRight, Cpu, Headphones, Gamepad2, Smartphone, Watch, Zap, Wrench, Shield } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { DiscountPurchase } from './types';

interface MyDiscountPurchasesListProps {
  purchases: DiscountPurchase[];
  className?: string;
}

const categoryIcons: Record<string, React.ReactNode> = {
  'Tecnología': <Cpu className="w-5 h-5" />,
  'Audio': <Headphones className="w-5 h-5" />,
  'Gaming': <Gamepad2 className="w-5 h-5" />,
  'Estuches': <Smartphone className="w-5 h-5" />,
  'Relojes': <Watch className="w-5 h-5" />,
  'Cargadores': <Zap className="w-5 h-5" />,
  'Reparaciones': <Wrench className="w-5 h-5" />,
  'Vidrios': <Shield className="w-5 h-5" />,
};

export const MyDiscountPurchasesList: React.FC<MyDiscountPurchasesListProps> = ({
  purchases,
  className,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Group purchases by category
  const categorySummary = useMemo(() => {
    const summary: Record<string, { total: number; count: number; purchases: DiscountPurchase[] }> = {};
    
    purchases.forEach(purchase => {
      if (!summary[purchase.category]) {
        summary[purchase.category] = { total: 0, count: 0, purchases: [] };
      }
      summary[purchase.category].total += purchase.final_price;
      summary[purchase.category].count += purchase.quantity;
      summary[purchase.category].purchases.push(purchase);
    });

    // Sort by total descending
    return Object.entries(summary)
      .sort((a, b) => b[1].total - a[1].total);
  }, [purchases]);

  const maxTotal = categorySummary.length > 0 ? categorySummary[0][1].total : 0;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const selectedPurchases = selectedCategory 
    ? categorySummary.find(([cat]) => cat === selectedCategory)?.[1].purchases || []
    : [];

  if (purchases.length === 0) {
    return (
      <div className={`bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag className="w-5 h-5 text-blue-400" />
          <h3 className="text-white font-bold">Categorías compradas</h3>
        </div>
        <p className="text-white/40 text-center py-8">No has registrado compras este mes</p>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10 ${className}`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white/60 text-sm">Categorías compradas</h3>
        </div>

        <div className="space-y-3">
          {categorySummary.map(([category, data], index) => (
            <motion.button
              key={category}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.05 * index }}
              onClick={() => setSelectedCategory(category)}
              className="w-full bg-white/5 rounded-xl p-3 border border-white/5 hover:border-purple-500/30 hover:bg-white/10 transition-all text-left group"
            >
              <div className="flex items-center gap-3">
                {/* Icon */}
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400 flex-shrink-0">
                  {categoryIcons[category] || <Tag className="w-5 h-5" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white font-medium">{category}</span>
                    <span className="text-green-400 font-bold">₡{data.total.toLocaleString()}</span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(data.total / maxTotal) * 100}%` }}
                      transition={{ delay: 0.1 * index, duration: 0.5 }}
                      className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                    />
                  </div>
                </div>

                {/* Count */}
                <div className="text-white/40 text-sm flex-shrink-0 flex items-center gap-1">
                  {data.count}x
                  <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Category Detail Dialog */}
      <Dialog open={!!selectedCategory} onOpenChange={() => setSelectedCategory(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400">
                {selectedCategory && (categoryIcons[selectedCategory] || <Tag className="w-5 h-5" />)}
              </div>
              <span>{selectedCategory}</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {selectedPurchases.map((purchase, index) => (
              <motion.div
                key={purchase.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                className="bg-white/5 rounded-xl p-4 border border-white/5"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-semibold">{purchase.product_name}</h4>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-bold">₡{purchase.final_price.toLocaleString()}</p>
                    <p className="text-white/40 text-xs line-through">₡{purchase.original_price.toLocaleString()}</p>
                  </div>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1.5 text-white/60">
                    <Percent className="w-3 h-3 text-blue-400" />
                    <span>{purchase.discount_percentage}% desc.</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/60">
                    <DollarSign className="w-3 h-3 text-green-400" />
                    <span>Ahorro: ₡{purchase.savings.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/60">
                    <Calendar className="w-3 h-3 text-purple-400" />
                    <span>{formatDate(purchase.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/60">
                    <ShoppingBag className="w-3 h-3 text-amber-400" />
                    <span>Cant: {purchase.quantity}</span>
                  </div>
                </div>

                {/* Notes if any */}
                {purchase.notes && (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <p className="text-white/40 text-xs italic">"{purchase.notes}"</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyDiscountPurchasesList;
